import { css } from '@emotion/react';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { TruelarkLogo } from '@frontend/promotion-assets#svg';
import { theme } from '@frontend/theme';
import { Button, DropdownField, FieldProps, SwitchField, Text, TextField, useTooltip } from '@frontend/design-system';
import { FormWrapper } from '../form-wrapper';
import { WebToTextFieldTypes } from './config';

export const EnableWebToText = (props: FieldProps<WebToTextFieldTypes, 'enableWebToText'>) => {
  const { t } = useTranslation('ai-receptionist-settings');

  return (
    <FormWrapper.Field>
      <SwitchField label={t('Enable Web-to-Text')} labelPlacement='left' {...props} />
      <Divider margin={0} color={theme.colors.neutral20} />
    </FormWrapper.Field>
  );
};

export const InstallationInstructions = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('ai-receptionist-settings');
  return (
    <FormWrapper.Field>
      <Text weight='bold'>{t('Installation')}</Text>
      <Button iconName='terminal' variant='secondary' onClick={onClick} css={{ maxWidth: 'fit-content' }}>
        {t('Installation Instructions')}
      </Button>{' '}
    </FormWrapper.Field>
  );
};

export const PreviewMessage = (props: FieldProps<WebToTextFieldTypes, 'previewMessage'>) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  return (
    <FormWrapper.Field>
      <div css={labelWithInfoStyle}>
        <Text weight='bold'>{t('Preview Message')}</Text>
        <span {...triggerProps}>
          <Icon name='info' size={16} color='default' />
        </span>
        <Tooltip {...tooltipProps} css={previewTooltipStyle}>
          <div css={previewTooltipContainerStyle}>
            <Text color='white' size='medium' weight='regular'>
              {t('This message pops up automatically when a page that has Web to Text installed.')}
            </Text>
            <Text
              color='white'
              size='medium'
              weight='bold'
              css={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(1) }}
            >
              {t('Example:')}
            </Text>
            <div css={chatBubbleContainerStyle}>
              <div css={logoBadgeStyle}>
                <TruelarkLogo width={24} height={24} color={theme.colors.indigo70} />
              </div>
              <div css={chatBubbleStyle}>
                <Text color='default' css={{ fontSize: '10px' }} weight='regular'>
                  {t("This week, we're offering 15% off our cleanings.")}
                  <br />
                  {t('Want to get scheduled?')}
                </Text>
              </div>
            </div>
          </div>
        </Tooltip>
      </div>
      <TextField {...props} label={t('Preview Message')} placeholder={t('Need to schedule or have a question?')} />
    </FormWrapper.Field>
  );
};

export const FirstMessageSentToUser = (props: FieldProps<WebToTextFieldTypes, 'firstMessageSentToUser'>) => {
  const { t } = useTranslation('ai-receptionist-settings');
  return (
    <FormWrapper.Field>
      <Text weight='bold'>{t('First Message Sent to User')}</Text>
      <TextField
        {...props}
        label={t('First Message Sent to User')}
        placeholder={t('Hi there! Welcome to Cascadia Clinic...')}
      />
    </FormWrapper.Field>
  );
};

export const ClinicName = (props: FieldProps<WebToTextFieldTypes, 'clinicName'>) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  return (
    <FormWrapper.Field>
      <div css={labelWithInfoStyle}>
        <Text weight='bold'>{t('Clinic Name')}</Text>
        <span {...triggerProps}>
          <Icon name='info' size={16} color='default' />
        </span>
        <Tooltip {...tooltipProps}>
          <Text color='white' size='medium' weight='regular' css={tooltipTextStyle}>
            {t('This name will appear at the top of the chat window.')}
          </Text>
        </Tooltip>
      </div>
      <TextField {...props} label={t('Clinic Name')} placeholder={t('Cascadia Clinic')} />
    </FormWrapper.Field>
  );
};

export const AssistantName = (props: FieldProps<WebToTextFieldTypes, 'assistantName'>) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  return (
    <FormWrapper.Field>
      <div css={labelWithInfoStyle}>
        <Text weight='bold'>{t('Assistant Name')}</Text>
        <span {...triggerProps}>
          <Icon name='info' size={16} color='default' />
        </span>
        <Tooltip {...tooltipProps}>
          <Text color='white' size='medium' weight='regular' css={tooltipTextStyle}>
            {t('Customize the name to give assistant personality.')}
          </Text>
        </Tooltip>
      </div>
      <TextField {...props} label={t('Assistant Name')} placeholder={t('Cascadia Assistant')} />
    </FormWrapper.Field>
  );
};

export const DefaultState = (props: FieldProps<WebToTextFieldTypes, 'defaultState'>) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  return (
    <FormWrapper.Field>
      <div css={labelWithInfoStyle}>
        <Text weight='bold'>{t('Default State')}</Text>
        <span {...triggerProps}>
          <Icon name='info' size={16} color='default' />
        </span>
        <Tooltip {...tooltipProps}>
          <div css={tooltipTextStyle}>
            <Text color='white' size='medium' weight='regular'>
              {t(
                'When open: Chat window will be fully open when the page loads, regardless of whether the visitor interacts. Keep in mind that this approach can be irritating for visitors and produce results opposite to what was intended.'
              )}
            </Text>
            <Text color='white' size='medium' weight='regular' css={{ marginTop: theme.spacing(2) }}>
              {t('When closed: Chat window will open fully only when visitors click to engage.')}
            </Text>
          </div>
        </Tooltip>
      </div>
      <DropdownField
        {...props}
        label={t('Default State')}
        placeholder={t('Select Default State')}
        css={css`
          width: 300px;
        `}
      >
        <DropdownField.Option value='collapsed'>{t('Closed')}</DropdownField.Option>
        <DropdownField.Option value='expanded'>{t('Open')}</DropdownField.Option>
      </DropdownField>
    </FormWrapper.Field>
  );
};

const labelWithInfoStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1.5)};
`;

const tooltipTextStyle = css`
  max-width: 224px;
`;

const previewTooltipStyle = css`
  padding: 0;
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
`;

const previewTooltipContainerStyle = css`
  background-color: #2f353b;
  padding: ${theme.spacing(2)};
  max-width: 320px;
  border-radius: ${theme.borderRadius.medium};
`;

const chatBubbleContainerStyle = css`
  display: flex;
  background-color: #4a515a;
  padding: ${theme.spacing(2)};
  gap: ${theme.spacing(1.5)};
  align-items: flex-end;
  margin-top: ${theme.spacing(1)};
`;

const logoBadgeStyle = css`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const chatBubbleStyle = css`
  background-color: ${theme.colors.white};
  padding: ${theme.spacing(1.5, 2)};
  border-radius: ${theme.spacing(1, 1, 1, 0)};
  color: ${theme.colors.neutral90};
`;
